import { useState } from 'react';
import { useSyncApplication, useSyncStatus } from '../hooks/useApi';
import { Button } from './ui';
import type { SyncInfo, SyncStatus } from '../types';
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  CloudOff,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';

interface SyncStatusCardProps {
  applicationId: number;
  reviewId?: number;
  syncInfo?: SyncInfo;
  srReviewId?: string | null;
  className?: string;
}

const statusConfig: Record<
  SyncStatus,
  {
    icon: typeof CheckCircle2;
    label: string;
    color: string;
    bgColor: string;
    description: string;
  }
> = {
  synced: {
    icon: CheckCircle2,
    label: 'Synced',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    description: 'Review has been synced to SmartRecruiters',
  },
  pending: {
    icon: Clock,
    label: 'Pending Sync',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Review is waiting to be synced',
  },
  error: {
    icon: AlertCircle,
    label: 'Sync Error',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    description: 'Sync failed - click to retry',
  },
  disabled: {
    icon: CloudOff,
    label: 'Sync Disabled',
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    description: 'Sync is disabled for this job',
  },
  no_review: {
    icon: XCircle,
    label: 'No Review',
    color: 'text-gray-400',
    bgColor: 'bg-gray-50',
    description: 'No review available to sync',
  },
};

export function SyncStatusCard({
  applicationId,
  reviewId,
  syncInfo,
  srReviewId,
  className = '',
}: SyncStatusCardProps) {
  const [showDetails, setShowDetails] = useState(false);
  const syncMutation = useSyncApplication();

  // Fetch detailed sync status if we have a reviewId
  const { data: detailedStatus, refetch: refetchStatus } = useSyncStatus(reviewId || 0);

  const status = syncInfo?.syncStatus || detailedStatus?.syncStatus || 'no_review';
  const canSync = syncInfo?.canSync ?? detailedStatus?.canSync ?? false;
  const isSynced = syncInfo?.isSynced ?? detailedStatus?.isSynced ?? false;
  const actualSrReviewId = srReviewId || detailedStatus?.srReviewId;

  const config = statusConfig[status];
  const Icon = config.icon;

  const handleSync = async (force: boolean = false) => {
    try {
      await syncMutation.mutateAsync({ applicationId, force });
      refetchStatus();
    } catch (err) {
      console.error('Sync failed:', err);
    }
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Sync Status</h3>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            {showDetails ? 'Hide details' : 'Show details'}
          </button>
        </div>

        {/* Status Display */}
        <div className={`flex items-center gap-3 p-3 rounded-lg ${config.bgColor}`}>
          <Icon className={`h-5 w-5 ${config.color}`} />
          <div className="flex-1">
            <p className={`text-sm font-medium ${config.color}`}>{config.label}</p>
            <p className="text-xs text-gray-600">{config.description}</p>
          </div>
        </div>

        {/* SR Review ID if synced */}
        {isSynced && actualSrReviewId && (
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <ExternalLink className="h-3 w-3" />
            <span>SR Review ID: {actualSrReviewId}</span>
          </div>
        )}

        {/* Detailed Status */}
        {showDetails && detailedStatus && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Application ID</span>
              <span className="text-gray-900">{detailedStatus.applicationId}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Job ID</span>
              <span className="text-gray-900">{detailedStatus.jobId}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Sync Enabled (Job)</span>
              <span className={detailedStatus.syncEnabled ? 'text-green-600' : 'text-red-600'}>
                {detailedStatus.syncEnabled ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Global Sync</span>
              <span className={detailedStatus.globalSyncEnabled ? 'text-green-600' : 'text-red-600'}>
                {detailedStatus.globalSyncEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {detailedStatus.lastError && (
              <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                {detailedStatus.lastError}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-4 flex gap-2">
          {canSync && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleSync(false)}
              loading={syncMutation.isPending}
              leftIcon={<RefreshCw className="h-4 w-4" />}
              className="flex-1"
            >
              Sync to SmartRecruiters
            </Button>
          )}
          {isSynced && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleSync(true)}
              loading={syncMutation.isPending}
              leftIcon={<RefreshCw className="h-4 w-4" />}
              className="flex-1"
            >
              Re-sync
            </Button>
          )}
          {status === 'error' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleSync(true)}
              loading={syncMutation.isPending}
              leftIcon={<RefreshCw className="h-4 w-4" />}
              className="flex-1"
            >
              Retry Sync
            </Button>
          )}
        </div>

        {/* Error Message from Mutation */}
        {syncMutation.isError && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            {syncMutation.error instanceof Error
              ? syncMutation.error.message
              : 'Sync failed. Please try again.'}
          </div>
        )}

        {/* Success Message */}
        {syncMutation.isSuccess && (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
            Sync job queued successfully. The review will be synced shortly.
          </div>
        )}
      </div>
    </div>
  );
}
